import { useQueryClient } from "@tanstack/react-query";
import Link from "next/link";
import { useRouter } from "next/router";

import { logout } from "../../core/retailShop.core";
import { useTranslation } from "../../core/translation.core";
import useAdminShop from "../../hooks/useAdminShop";
import CashIcon from "../../icons/posicons/cash.icon";
import CustomersIcon from "../../icons/posicons/customers.icon";
import DocumentDuplicateIcon from "../../icons/posicons/documentDuplicate.icon";
import TruckIcon from "../../icons/truck.icon";
import LogoutIcon from "../../icons/posicons/logout.icon";
import NewOrderIcon from "../../icons/posicons/neworder.icon";
import OrderListIcon from "../../icons/posicons/orderList.icon";
import ProductsIcon from "../../icons/posicons/products.icon";
import SettingIcon from "../../icons/posicons/setting.icon";
import UserName from "../reuseables/userName.comp";

const iconStyle = "h-8 w-8";
const linkStyles = "cursor-pointer text-xs opacity-90 font-medium flex flex-col gap-1 items-center dark:text-white w-full";

const activeLinkStyles =
  "cursor-pointer " +
  "font-semibold rounded-xl " +
  "text-[#3878F7] text-xs opacity-90 " +
  "bg-[#F5F9FE] py-2 px-1 " +
  "flex flex-col items-center";

export default function PosNav({ className }: { className?: string }) {
  const { t, msg } = useTranslation();
  const queryClient = useQueryClient();
  const shop = useAdminShop();

  return (
    <section
      // 61+16+16
      className={`${className} bg-white dark:bg-her-darkblue2 overflow-y-scroll h-screen w-[90px] pt-6 pb-4 shadow-sm dark:shadow-lg relative`}
    >
      <div className="flex flex-col gap-12  justify-between h-full">
        <div className="space-y-12">
          <Item id="new-order" href="/" activeAddCss="text-her-lightFrenchBeige">
            <NewOrderIcon className={`${iconStyle} text-her-lightFrenchBeige`} />
            <span className="text-center">{t(msg.posMenuItems.newOrder)}</span>
          </Item>

          <Item id="order" href="/orders" activeAddCss="text-her-blue">
            <OrderListIcon className={`${iconStyle} text-her-blue`} />
            <span className="text-center">{t(msg.posMenuItems.orders)}</span>
          </Item>

          <Item id="product" href="/products" activeAddCss="text-her-mysticMaroon">
            <ProductsIcon className={`${iconStyle} text-her-mysticMaroon`} />
            <span className="text-center">{t(msg.posMenuItems.products)}</span>
          </Item>

          <Item id="warehouse-transfer" href="/warehouseTransfer" activeAddCss="text-[#5C7CFA]">
            <DocumentDuplicateIcon className={`${iconStyle} text-[#5C7CFA]`} />
            <span className="text-center">{t(msg.posMenuItems.warehouseTransfer)}</span>
          </Item>

          <Item id="settlement" href="/settlement" activeAddCss="text-her-robinEggBlue">
            <CashIcon className={`${iconStyle} text-her-robinEggBlue`} />
            <span className="text-center">{t(msg.posMenuItems.settlement)}</span>
          </Item>

          <Item id="customer" href="/customers" activeAddCss="text-her-paradisePink">
            <CustomersIcon className={`${iconStyle} text-her-paradisePink`} />
            <span className="text-center">{t(msg.posMenuItems.customers)}</span>
          </Item>

          {shop?.posDeliveryMenuEnabled && (
            <Item id="delivery-schedules" href="/deliveryPickup" activeAddCss="text-her-mysticMaroon">
              <TruckIcon className={`${iconStyle} text-her-mysticMaroon`} />
              <span className="text-center">{t(msg.posMenuItems.deliveryPickup)}</span>
            </Item>
          )}

          <Item id="setting" href="/setting" activeAddCss="text-her-lightFrenchBeige">
            <SettingIcon className={`${iconStyle} text-her-lightFrenchBeige`} />
            <span className="text-center">{t(msg.posMenuItems.setting)}</span>
          </Item>
        </div>

        <div className="flex flex-col items-center gap-6">
          <UserName />

          <button
            className="flex-center pb-10 flex-col text-[#222222] text-xs dark:text-white"
            onClick={() => logout(queryClient)}
          >
            <LogoutIcon className={`${iconStyle} text-her-mysticMaroon`} />
            <span className="pt-2">{t(msg.menu.logout)}</span>
          </button>
        </div>
      </div>
    </section>
  );
}

const Item = ({
  id,
  href,
  children,
  activeAddCss,
}: {
  id?: string;
  href?: string;
  children: React.ReactNode;
  activeAddCss?: string;
}) => {
  const { pathname } = useRouter();

  const isActive =
    href === "/"
      ? pathname === "/"
      : Boolean(href && pathname.startsWith(href));

  return (
    <Link id={id} href={href!} className={isActive ? activeLinkStyles + " " + activeAddCss : linkStyles}>
      {children}
    </Link>
  );
};
